<template>
  <div class="dashboard-wrapper" style="min-height: 100vh">
    <v-overlay :value="!studioConfirmed" opacity="0.98" z-index="999" color="#050505">
      <div style="width: 100vw; height: 100vh" class="d-flex align-center justify-center">
        <v-row justify="center" no-gutters style="width: 100%">
          <v-col cols="11" sm="8" md="6" lg="5" xl="4">
            <v-card class="rounded-xl pa-8 text-center glass-card border-glow-entry" elevation="24" width="100%">
              <v-avatar color="#00963f" size="90" class="mb-6 elevation-10 shadow-glow">
                <span class="white--text text-h3 font-weight-bold">{{ username.charAt(0) }}</span>
              </v-avatar>

              <h2 class="text-h4 font-weight-bold white--text mb-2">Willkommen</h2>
              <div class="text-subtitle-1 grey--text text--lighten-1 mb-8">
                {{ username }}
              </div>

              <div class="mb-2 primary-green--text text-caption text-uppercase font-weight-bold letter-spacing-2">Dein Studio</div>
              <v-sheet color="rgba(0, 150, 63, 0.1)" class="rounded-lg py-4 mb-8" style="border: 1px solid rgba(0, 150, 63, 0.3)">
                <v-icon left color="#00963f">mdi-domain</v-icon>
                <span class="font-weight-bold white--text text-h6">{{ userStudio }}</span>
              </v-sheet>

              <v-btn
                block
                x-large
                color="#00963f"
                class="mb-4 rounded-lg font-weight-bold white--text shadow-glow btn-hover-scale"
                @click="studioConfirmed = true">
                Starten <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>

              <div class="d-flex justify-space-between mt-4 px-2">
                <v-btn text small color="grey" @click="showStudioSelection"> Wechseln </v-btn>
                <v-btn text small color="error" @click="signOut"> Abmelden </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-overlay>

    <v-fade-transition mode="out-in">
      <div v-if="studioConfirmed" class="pt-6 px-4 px-md-8" style="max-width: 1800px; margin: 0 auto">
        <v-row>
          <v-col cols="12" md="8" lg="8" xl="9">
            <div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-space-between mb-6">
              <div>
                <div class="text-overline grey--text mb-n1">Zeiterfassung</div>
                <h1 class="text-h4 font-weight-bold white--text d-flex align-center">
                  <v-icon large color="#00963f" class="mr-3">mdi-calendar-today</v-icon>
                  {{ calendarType === "month" ? $moment(selectedDate).format("MMMM YYYY") : $moment(selectedDate).format("dddd, DD.MM.") }}
                </h1>
              </div>

              <v-sheet color="#1a1a1a" class="rounded-lg d-flex mt-4 mt-sm-0" elevation="0">
                <v-btn
                  v-for="type in calendarTypes"
                  :key="type.value"
                  :color="calendarType === type.value ? '#00963f' : 'transparent'"
                  :class="calendarType === type.value ? 'white--text shadow-glow' : 'grey--text'"
                  class="rounded-md transition-swing"
                  small
                  depressed
                  @click="calendarType = type.value">
                  {{ type.text }}
                </v-btn>
              </v-sheet>
            </div>

            <v-slide-y-transition>
              <v-alert
                v-if="submissionState"
                :color="submissionState.state === 'accepted' ? '#00963f' : '#FF9800'"
                dark
                class="rounded-lg mb-6 shadow-soft"
                icon="mdi-information"
                prominent>
                <div class="d-flex align-center justify-space-between w-100">
                  <div>
                    <strong class="text-subtitle-1">{{ submissionState.state === "accepted" ? "Monat abgeschlossen" : "Eingereicht" }}</strong>
                    <div class="text-caption opacity-80">
                      {{ submissionState.state === "accepted" ? "Genehmigt durch Centerleitung." : "Wartet auf Prüfung." }}
                    </div>
                  </div>
                </div>
              </v-alert>
            </v-slide-y-transition>

            <v-slide-y-transition>
              <div v-if="pendingSickEntries && pendingSickEntries.length > 0">
                <v-alert
                  v-for="entry in pendingSickEntries"
                  :key="entry._id"
                  color="warning"
                  dark
                  class="rounded-lg mb-6 shadow-soft text-left"
                  icon="mdi-alert-circle"
                  prominent>
                  <div class="d-flex align-center justify-space-between w-100 flex-wrap">
                    <div>
                      <strong class="text-subtitle-1">Ausstehende Krankmeldung / Abwesenheit</strong>
                      <div class="text-caption opacity-80">
                        Du wurdest am {{ formatSickDaysText(entry.sickDays) }} als krank/abwesend gemeldet oder eine Schicht wurde übertragen. Bitte
                        bestätige diese Abwesenheit.
                      </div>
                    </div>
                    <v-btn class="ml-sm-4 mt-2 mt-sm-0 font-weight-bold" color="white" light @click="openRespondDialog(entry)"> Antworten </v-btn>
                  </div>
                </v-alert>
              </div>
            </v-slide-y-transition>

            <v-row class="mb-4" dense>
              <v-col cols="6" sm="3">
                <div class="action-btn-wrapper h-100">
                  <div class="d-flex align-center justify-center flex-column py-2">
                    <enter-holidays @fetchEntries="fetchEntries" :holidayEntries="holidayEntries"></enter-holidays>
                    <div class="action-label">Ferien</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="action-btn-wrapper h-100">
                  <div class="d-flex align-center justify-center flex-column py-2">
                    <enter-free-wishes @fetchEntries="fetchEntries" :freeWishes="freeWishes"></enter-free-wishes>
                    <div class="action-label">Freiwunsch</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="action-btn-wrapper h-100">
                  <div class="d-flex align-center justify-center flex-column py-2">
                    <enter-shift-wishes></enter-shift-wishes>
                    <div class="action-label">Schichtwunsch</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="action-btn-wrapper h-100" @click="showAbsencesDialog = true" style="cursor: pointer">
                  <div class="d-flex align-center justify-center flex-column py-2">
                    <v-btn icon large>
                      <v-icon color="white">mdi-calendar-clock</v-icon>
                    </v-btn>
                    <div class="action-label">Abwesenheiten</div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row v-if="calendarType === 'day'" class="mb-2 match-height">
              <v-col cols="12" sm="6">
                <v-card class="rounded-xl pa-6 fill-height gradient-green elevation-10 card-hover-lift d-flex flex-column justify-space-between" dark>
                  <div class="d-flex justify-space-between align-start">
                    <div>
                      <div class="text-caption white--text opacity-80 mb-1 font-weight-bold letter-spacing-1">ARBEITSZEIT</div>
                      <div class="text-h2 font-weight-bold white--text mt-2">
                        {{ dayWorkTime.formattedTime }}<span class="text-h5 opacity-60 ml-1">h</span>
                      </div>
                    </div>
                    <v-sheet color="rgba(255,255,255,0.2)" class="rounded-circle pa-3 backdrop-blur">
                      <v-icon color="white" large>mdi-clock-time-four-outline</v-icon>
                    </v-sheet>
                  </div>
                  <div>
                    <v-progress-linear
                      :value="(dayWorkTime.minutes / 504) * 100"
                      color="white"
                      background-opacity="0.2"
                      rounded
                      height="6"
                      class="mt-6 mb-2"></v-progress-linear>
                    <div class="d-flex justify-space-between text-caption font-weight-medium opacity-90">
                      <span>{{ dayWorkStart }} Uhr</span>
                      <span>{{ dayWorkEnd }} Uhr</span>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" sm="6">
                <v-card class="rounded-xl fill-height pa-6 glass-card card-hover-lift d-flex flex-column justify-space-between" elevation="0">
                  <div class="d-flex justify-space-between align-start">
                    <div>
                      <div class="text-caption grey--text mb-1 font-weight-bold letter-spacing-1">PAUSE</div>
                      <div class="text-h2 font-weight-bold orange--text">{{ dayPauseTime }}<span class="text-h5 grey--text ml-1">h</span></div>
                    </div>
                    <v-sheet color="rgba(255, 152, 0, 0.1)" class="rounded-circle pa-3">
                      <v-icon color="orange" large>mdi-coffee-outline</v-icon>
                    </v-sheet>
                  </div>
                  <div class="mt-4 d-flex align-center">
                    <break-rules></break-rules>
                    <v-spacer></v-spacer>
                    <span class="text-caption grey--text"><v-icon x-small color="grey" class="mr-1">mdi-information-outline</v-icon>Regelung</span>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-textarea
              v-if="userService.hasRight('tt_daily_note') && calendarType == 'day'"
              v-model="dailyNote"
              @blur="saveDailyNote()"
              solo
              flat
              background-color="#151515"
              class="rounded-lg mt-4 white--text border-light"
              label="Tagesnotiz"
              hide-details
              rows="3"
              auto-grow
              dark></v-textarea>

            <v-col class="px-0" cols="12" v-if="$vuetify.breakpoint.mobile">
              <div style="position: sticky; top: 24px">
                <v-card
                  v-if="calendarType === 'day' && $moment(selectedDate).format('DD-MM-YYYY') === $moment().format('DD-MM-YYYY') && showButtons"
                  class="rounded-xl d-flex flex-column align-center text-center mb-6 glass-card elevation-10"
                  elevation="0"
                  style="border-top: 4px solid #00963f">
                  <div class="text-overline grey--text letter-spacing-2 mb-4">ZEITERFASSUNG</div>

                  <div v-if="currentType !== 'checkin' && currentType !== 'pause' && currentType !== 'pauseend'" class="py-4">
                    <v-btn
                      class="pulse-green mb-6"
                      fab
                      x-large
                      color="#00963f"
                      elevation="20"
                      height="160"
                      width="160"
                      @click="createTimeEntry('checkin')"
                      :disabled="isMonthLocked"
                      :loading="loading">
                      <v-icon size="70" color="white">mdi-power</v-icon>
                    </v-btn>
                    <div class="white--text text-h5 font-weight-bold">Start</div>
                    <div class="grey--text text-caption mt-1">Tippen zum Einchecken</div>
                  </div>

                  <div v-else class="w-100 py-2">
                    <div class="d-flex align-center justify-center mb-8">
                      <div class="timer-dot pulse-dot mr-3"></div>
                      <div class="text-h3 font-weight-bold white--text">Aktiv</div>
                    </div>

                    <v-btn
                      block
                      x-large
                      color="#1e1e1e"
                      class="rounded-lg mb-4 red--text text--accent-2 font-weight-bold shadow-soft py-8"
                      @click="createTimeEntry('checkout')"
                      :loading="loading"
                      :disabled="currentType === 'pause' || currentType === 'smoke' || isMonthLocked"
                      style="border: 1px solid #ff5252">
                      <v-icon left large>mdi-stop</v-icon> Check-Out
                    </v-btn>

                    <v-row dense class="mt-2">
                      <v-col cols="6" v-if="currentType === 'checkin'">
                        <v-btn
                          block
                          large
                          color="rgba(255, 152, 0, 0.15)"
                          class="rounded-lg orange--text py-6"
                          @click="createTimeEntry('pause')"
                          :disabled="isMonthLocked"
                          :loading="loading">
                          <v-icon left>mdi-coffee</v-icon> Pause
                        </v-btn>
                      </v-col>
                      <v-col cols="6" v-if="currentType === 'checkin'">
                        <v-btn
                          block
                          large
                          color="#2c2c2c"
                          class="rounded-lg grey--text py-6"
                          @click="createTimeEntry('smoke')"
                          :disabled="isMonthLocked"
                          :loading="loading">
                          <v-icon left>mdi-smoking</v-icon> Rauchen
                        </v-btn>
                      </v-col>
                      <v-col cols="12" v-if="currentType === 'pause'">
                        <v-btn
                          block
                          large
                          color="#00963f"
                          class="rounded-lg white--text shadow-glow py-6"
                          @click="createTimeEntry('pauseend')"
                          :disabled="isMonthLocked"
                          :loading="loading">
                          <v-icon left>mdi-play</v-icon> Weiter
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                </v-card>

                <div v-if="calendarType === 'month'" class="text-center mb-6">
                  <v-btn
                    v-if="!isMonthLocked"
                    block
                    large
                    color="#00963f"
                    class="rounded-lg white--text shadow-glow mb-4 py-6"
                    @click="submitForSelectedMonth()">
                    <v-icon left>mdi-check-circle-outline</v-icon> Monat Abschliessen
                  </v-btn>
                  <v-btn v-else text color="orange" disabled>
                    <v-icon class="orange--text" left>mdi-lock</v-icon> <span class="orange--text">Bereits eingereicht </span></v-btn
                  >
                </div>
                <v-btn
                  v-if="calendarType !== 'day' || $moment(selectedDate).format('DD-MM-YYYY') !== $moment().format('DD-MM-YYYY')"
                  block
                  text
                  large
                  class="glass-btn rounded-lg"
                  color="grey"
                  @click="
                    () => {
                      selectedDate = new Date()
                      calendarType = 'day'
                      fetchEntries()
                    }
                  ">
                  <v-icon left>mdi-calendar-today</v-icon>
                  Zurück zu Heute
                </v-btn>

                <div v-if="calendarType === 'day'" class="d-none d-md-flex flex-column align-center pa-4">
                  <edit-time-entry
                    v-if="showButtons && !isMonthLocked"
                    @fetchEntries="fetchEntries"
                    :day="$moment(selectedDate).format('YYYY-MM-DD')"
                    :recentEntries="recentEntries"></edit-time-entry>

                  <demand-time-entry-edit
                    v-if="!showButtons && (!submissionState || (submissionState && submissionState.state === 'rejected'))"
                    @fetchEntries="fetchEntries"
                    :selectedDate="selectedDate"></demand-time-entry-edit>

                  <v-btn
                    block
                    large
                    outlined
                    color="grey"
                    class="mt-2 rounded-lg font-weight-bold"
                    v-if="!submissionState || (submissionState && submissionState.state === 'rejected')"
                    @click="fetchEntries(!showCorrection)">
                    <v-icon left>{{ showCorrection ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                    {{ showCorrection ? "Korrektur ausblenden" : "Korrekturmodus" }}
                  </v-btn>
                </div>
              </div>
            </v-col>
            <v-row no-gutters v-if="calendarType === 'month'" class="mb-4 mt-2">
              <v-col cols="12">
                <v-card class="rounded-xl pa-6 gradient-dark elevation-0" style="border: 1px solid #333">
                  <v-row align="center">
                    <v-col cols="12" :md="employeeStudio.noFixPercentage ? '12' : '3'" class="text-center">
                      <div class="text-h3 font-weight-bold white--text">{{ dayWorkTime.formattedTime }}</div>
                      <div class="text-caption primary-green--text">IST-STUNDEN</div>
                    </v-col>
                    <v-col v-if="!employeeStudio.noFixPercentage" cols="12" md="3" class="text-center">
                      <div class="text-h4 grey--text">
                        {{ Math.round((calcBusinessDays - vacationDaysForMonth().length) * ((8.4 * currentMonthPercentage) / 100) * 1000) / 1000 }}
                        h
                      </div>
                      <div class="text-caption grey--text">
                        SOLL ({{ currentMonthPercentage }}%)
                        <v-chip v-if="isPartialMonthEntry" x-small color="info" outlined class="ml-1" style="height: 16px">
                          Eintritt {{ $moment(employeeStudio.entryDate).format("DD.MM.") }}
                        </v-chip>
                        <v-chip v-if="isPartialMonthExit" x-small color="warning" outlined class="ml-1" style="height: 16px">
                          Austritt {{ $moment(employeeStudio.exitDate).format("DD.MM.") }}
                        </v-chip>
                      </div>
                      <!-- Feiertage Auflistung -->
                      <div v-if="vacationDaysForMonth().length > 0" class="mt-2">
                        <v-tooltip bottom max-width="260">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              x-small
                              color="rgba(255,255,255,0.08)"
                              text-color="grey lighten-1"
                              v-bind="attrs"
                              v-on="on"
                              style="cursor: pointer">
                              <v-icon x-small left color="grey lighten-1">mdi-calendar-star</v-icon>
                              {{ vacationDaysForMonth().length }} Feiertag{{ vacationDaysForMonth().length > 1 ? "e" : "" }}
                              abgezogen
                            </v-chip>
                          </template>
                          <div>
                            <div class="font-weight-bold mb-1" style="font-size: 12px">Abgezogene Feiertage:</div>
                            <div v-for="hol in vacationDaysForMonth()" :key="hol.date" style="font-size: 11px; line-height: 1.6">
                              📅 {{ $moment(hol.date).format("dd, DD.MM.") }} – {{ hol.title }}
                            </div>
                          </div>
                        </v-tooltip>
                      </div>
                    </v-col>
                    <v-col v-if="!employeeStudio.noFixPercentage" cols="12" md="3" class="text-center">
                      <div class="text-h3 font-weight-bold" :class="parseFloat(timeDifference) >= 0 ? 'success--text' : 'error--text'">
                        {{ Math.round(parseFloat(timeDifference) * 100) / 100 }}
                      </div>
                      <div class="text-caption grey--text">DIFFERENZ</div>
                    </v-col>
                    <v-col v-if="!employeeStudio.noFixPercentage" cols="12" md="3" class="text-center">
                      <div class="text-h4" :class="overTime >= 0 ? 'success--text' : 'orange--text'">
                        {{ overTime }}
                      </div>
                      <div class="text-caption grey--text">ÜBERSTUNDEN SALDO</div>
                      <div class="text-caption grey--text">
                        Stand
                        {{
                          timeTrackerMonths && timeTrackerMonths.findLast((entry) => entry.state === "accepted")
                            ? this.$moment(timeTrackerMonths.findLast((entry) => entry.state === "accepted").month).format("MMM YYYY")
                            : "Keine abgeschlossene Zeiterfassung gefunden"
                        }}
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
            <div class="d-md-none mt-6 mb-4">
              <div v-if="calendarType === 'day'" class="d-flex flex-column align-center gap-2">
                <edit-time-entry
                  v-if="showButtons"
                  @fetchEntries="fetchEntries"
                  :day="$moment(selectedDate).format('YYYY-MM-DD')"
                  :recentEntries="recentEntries"></edit-time-entry>

                <demand-time-entry-edit
                  v-if="!showButtons && (!submissionState || (submissionState && submissionState.state === 'rejected'))"
                  @fetchEntries="fetchEntries"
                  :selectedDate="selectedDate"></demand-time-entry-edit>

                <v-btn
                  block
                  large
                  outlined
                  color="grey"
                  class="mt-2 rounded-lg font-weight-bold"
                  v-if="!submissionState || (submissionState && submissionState.state === 'rejected')"
                  @click="fetchEntries(!showCorrection)">
                  <v-icon left>{{ showCorrection ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                  {{ showCorrection ? "Korrektur ausblenden" : "Korrekturmodus" }}
                </v-btn>
              </div>
            </div>
            <v-card class="rounded-xl mt-2 glass-card overflow-hidden" elevation="0">
              <div class="d-flex align-center pa-4 border-bottom-dark">
                <v-btn icon @click="previousDate" class="glass-btn"><v-icon color="white">mdi-chevron-left</v-icon></v-btn>
                <v-spacer></v-spacer>
                <v-menu v-model="showDatePicker" :close-on-content-click="false" :z-index="30" bottom offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn text class="white--text font-weight-bold text-h6" v-bind="attrs" v-on="on" aria-label="Datum auswählen">
                      <span v-if="calendarType === 'month'">
                        {{ $moment(selectedDate).startOf("month").format("DD.") }} - {{ $moment(selectedDate).endOf("month").format("DD.MM.YYYY") }}
                      </span>
                      <span v-else-if="calendarType === 'week'">KW {{ $moment(selectedDate).week() }}</span>
                      <span v-else>{{ $moment(selectedDate).format("DD.MM.YYYY") }}</span>
                    </v-btn>
                  </template>
                  <v-date-picker
                    first-day-of-week="1"
                    locale="de-CH"
                    v-model="selectedDateFormatted"
                    @input="updateSelectedDate"
                    @change="showDatePicker = false"
                    color="#00963f"></v-date-picker>
                </v-menu>
                <v-spacer></v-spacer>
                <v-btn icon @click="nextDate" class="glass-btn"><v-icon color="white">mdi-chevron-right</v-icon></v-btn>
              </div>

              <v-sheet color="transparent" class="pa-0 calendar-wrapper" style="min-height: 500px">
                <v-calendar
                  ref="calendar"
                  dark
                  v-model="selectedDate"
                  :type="calendarType"
                  :weekdays="weekdays"
                  :events="filteredEvents"
                  :event-color="getEventColor"
                  @click:event="handleEventClick"
                  @click:date="handleDateClick"
                  color="#00963f">
                  <template #event="{ event }">
                    <v-tooltip bottom open-delay="200" max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <div
                          v-on="on"
                          v-bind="attrs"
                          v-if="calendarType == 'day'"
                          class="fill-height rounded-lg pl-2 py-1 event-stripe"
                          :style="`border-left: 4px solid ${event.color || '#00963f'}; background: rgba(30,30,30,0.8);`">
                          <div class="font-weight-bold text-body-2 white--text">
                            {{ $moment(event.start).format("HH:mm") }} - {{ event.end ? $moment(event.end).format("HH:mm") : "..." }}
                          </div>
                          <div class="text-caption grey--text text--lighten-1 text-truncate">
                            {{ event.details }}
                          </div>
                        </div>
                        <div v-else v-on="on" v-bind="attrs" class="pl-1 text-truncate font-weight-medium" style="font-size: 11px">
                          {{ event.details }}
                        </div>
                      </template>
                      <span>{{ event.details }}</span>
                    </v-tooltip>
                  </template>
                </v-calendar>
              </v-sheet>
            </v-card>

            <div class="mt-4">
              <trip-control-history
                v-if="
                  userService.hasRight('tt_tripControlEntry') &&
                  calendarType == 'day' &&
                  $moment(selectedDate).format('DD-MM-YYYY') === $moment().format('DD-MM-YYYY')
                "
                @fetchEntries="fetchEntries"
                :tripControlHistoryEntries="tripControlHistoryEntries"></trip-control-history>
            </div>
          </v-col>

          <v-col v-if="!$vuetify.breakpoint.mobile" cols="12" md="4" lg="4" xl="3">
            <div style="position: sticky; top: 24px">
              <v-card
                v-if="calendarType === 'day' && $moment(selectedDate).format('DD-MM-YYYY') === $moment().format('DD-MM-YYYY') && showButtons"
                class="rounded-xl pa-6 d-flex flex-column align-center text-center mb-6 glass-card elevation-10"
                elevation="0"
                style="border-top: 4px solid #00963f">
                <div class="text-overline grey--text letter-spacing-2 mb-4">ZEITERFASSUNG</div>

                <div v-if="currentType !== 'checkin' && currentType !== 'pause' && currentType !== 'pauseend'" class="py-4">
                  <v-btn
                    class="pulse-green mb-6"
                    fab
                    x-large
                    color="#00963f"
                    elevation="20"
                    height="160"
                    width="160"
                    @click="createTimeEntry('checkin')"
                    :loading="loading">
                    <v-icon size="70" color="white">mdi-power</v-icon>
                  </v-btn>
                  <div class="white--text text-h5 font-weight-bold">Start</div>
                  <div class="grey--text text-caption mt-1">Tippen zum Einchecken</div>
                </div>

                <div v-else class="w-100 py-2">
                  <div class="d-flex align-center justify-center mb-8">
                    <div class="timer-dot pulse-dot mr-3"></div>
                    <div class="text-h3 font-weight-bold white--text">Aktiv</div>
                  </div>

                  <v-btn
                    block
                    x-large
                    color="#1e1e1e"
                    class="rounded-lg mb-4 red--text text--accent-2 font-weight-bold shadow-soft py-8"
                    @click="createTimeEntry('checkout')"
                    :loading="loading"
                    :disabled="currentType === 'pause' || currentType === 'smoke'"
                    style="border: 1px solid #ff5252">
                    <v-icon left large>mdi-stop</v-icon> Check-Out
                  </v-btn>

                  <v-row dense class="mt-2">
                    <v-col cols="6" v-if="currentType === 'checkin'">
                      <v-btn
                        block
                        large
                        color="rgba(255, 152, 0, 0.15)"
                        class="rounded-lg orange--text py-6"
                        @click="createTimeEntry('pause')"
                        :loading="loading">
                        <v-icon left>mdi-coffee</v-icon> Pause
                      </v-btn>
                    </v-col>
                    <v-col cols="6" v-if="currentType === 'checkin'">
                      <v-btn block large color="#2c2c2c" class="rounded-lg grey--text py-6" @click="createTimeEntry('smoke')" :loading="loading">
                        <v-icon left>mdi-smoking</v-icon> Rauchen
                      </v-btn>
                    </v-col>
                    <v-col cols="12" v-if="currentType === 'pause'">
                      <v-btn
                        block
                        large
                        color="#00963f"
                        class="rounded-lg white--text shadow-glow py-6"
                        @click="createTimeEntry('pauseend')"
                        :loading="loading">
                        <v-icon left>mdi-play</v-icon> Weiter
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </v-card>

              <div v-if="calendarType === 'month'" class="text-center mb-6">
                <v-btn
                  v-if="
                    !selfSubmittedTimetrackerMonths.find((month) => month === $moment(selectedDate).format('YYYY-MM')) ||
                    selfSubmittedTimetrackerMonths.find((month) => month === $moment(selectedDate).format('YYYY-MM')).state === 'onlyNotes'
                  "
                  block
                  large
                  color="#00963f"
                  class="rounded-lg white--text shadow-glow mb-4 py-6"
                  @click="submitForSelectedMonth()">
                  <v-icon left>mdi-check-circle-outline</v-icon> Monat Abschliessen
                </v-btn>
                <v-btn v-else text color="orange" disabled> <v-icon left>mdi-lock</v-icon> Bereits eingereicht </v-btn>
              </div>

              <v-btn
                v-if="calendarType !== 'day' || $moment(selectedDate).format('DD-MM-YYYY') !== $moment().format('DD-MM-YYYY')"
                block
                text
                large
                class="glass-btn rounded-lg"
                color="grey"
                @click="
                  () => {
                    selectedDate = new Date()
                    calendarType = 'day'
                    fetchEntries()
                  }
                ">
                <v-icon left>mdi-calendar-today</v-icon>
                Zurück zu Heute
              </v-btn>

              <div v-if="calendarType === 'day'" class="d-none d-md-flex flex-column align-center pa-4">
                <edit-time-entry
                  v-if="showButtons"
                  @fetchEntries="fetchEntries"
                  :day="$moment(selectedDate).format('YYYY-MM-DD')"
                  :recentEntries="recentEntries"></edit-time-entry>

                <demand-time-entry-edit
                  v-if="!showButtons && (!submissionState || (submissionState && submissionState.state === 'rejected'))"
                  @fetchEntries="fetchEntries"
                  :selectedDate="selectedDate"></demand-time-entry-edit>

                <v-btn
                  block
                  large
                  outlined
                  color="grey"
                  class="mt-2 rounded-lg font-weight-bold"
                  v-if="!submissionState || (submissionState && submissionState.state === 'rejected')"
                  @click="fetchEntries(!showCorrection)">
                  <v-icon left>{{ showCorrection ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                  {{ showCorrection ? "Korrektur ausblenden" : "Korrekturmodus" }}
                </v-btn>
              </div>

              <!-- Absences Overview Card (Desktop only) -->
              <v-card class="rounded-xl pa-6 glass-card mt-6 elevation-10 text-left" elevation="0">
                <div class="d-flex align-center mb-4">
                  <v-icon color="#FFC107" class="mr-2">mdi-calendar-clock</v-icon>
                  <h3 class="text-h6 font-weight-bold white--text">Meine Abwesenheiten</h3>
                </div>

                <div v-if="allAbsences && allAbsences.length > 0" class="custom-scrollbar" style="max-height: 350px; overflow-y: auto">
                  <div
                    v-for="abs in allAbsences"
                    :key="abs.id"
                    class="pa-3 rounded-lg mb-2 border-light text-left"
                    style="background: rgba(255, 255, 255, 0.02)">
                    <div class="d-flex align-center justify-space-between mb-1">
                      <span class="font-weight-bold white--text" style="font-size: 0.85rem">{{ abs.typeText }}</span>
                      <v-chip
                        x-small
                        :color="abs.status === 'accepted' ? 'success' : abs.status === 'rejected' ? 'error' : 'warning'"
                        class="font-weight-black">
                        {{ abs.status === "accepted" ? "Genehmigt" : abs.status === "rejected" ? "Abgelehnt" : "Ausstehend" }}
                      </v-chip>
                    </div>
                    <div class="grey--text text-caption font-weight-medium" style="font-size: 0.75rem !important">
                      {{ abs.dateText }} ({{ abs.details }})
                    </div>
                    <div
                      v-if="abs.note"
                      class="grey--text text-caption mt-1 font-italic text-truncate"
                      style="font-size: 0.7rem !important"
                      :title="abs.note">
                      "{{ abs.note }}"
                    </div>
                    <!-- Arztzeugnis anzeigen falls vorhanden -->
                    <div
                      v-if="abs.attachment"
                      class="mt-2 pt-2 d-flex align-center justify-space-between"
                      style="border-top: 1px dashed rgba(255, 255, 255, 0.1)">
                      <div class="d-flex align-center">
                        <v-icon x-small color="info" class="mr-1">
                          {{ abs.attachment.mimeType && abs.attachment.mimeType.includes("pdf") ? "mdi-file-pdf-box" : "mdi-file-image" }}
                        </v-icon>
                        <span
                          class="grey--text font-weight-medium text-truncate"
                          style="font-size: 0.7rem !important; max-width: 110px"
                          :title="abs.attachment.originalName">
                          {{ abs.attachment.originalName }}
                        </span>
                      </div>
                      <v-btn
                        x-small
                        color="#00963f"
                        class="black--text font-weight-bold"
                        @click="downloadAttachment(abs)"
                        :loading="downloadingAbsId === abs.id">
                        Anzeigen
                      </v-btn>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center py-6 grey--text text-caption">Keine Abwesenheiten vorhanden.</div>
              </v-card>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-fade-transition>

    <v-dialog v-model="showSubmitDialog" max-width="450" overlay-opacity="0.90" overlay-color="#050505">
      <div style="position: relative; padding-top: 45px">
        <div class="d-flex justify-center" style="position: absolute; top: 0; left: 0; right: 0; z-index: 2">
          <v-avatar size="90" class="elevation-10 shadow-glow icon-ring" style="background: linear-gradient(135deg, #00963f 0%, #005c27 100%)">
            <v-icon x-large color="white">mdi-file-document-check-outline</v-icon>
          </v-avatar>
        </div>

        <v-card class="rounded-xl glass-card text-center pb-6 pt-10 px-6 overflow-visible" elevation="24">
          <div
            style="
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 120px;
              background: linear-gradient(to bottom, rgba(0, 150, 63, 0.15) 0%, transparent 100%);
              z-index: 0;
              border-radius: 24px 24px 0 0;
            "></div>

          <div style="position: relative; z-index: 1">
            <h3 class="text-h4 font-weight-bold white--text mb-2 text-shadow mt-4">Monat abschliessen?</h3>

            <div class="text-body-1 grey--text text--lighten-1 mb-8">
              Du bist dabei, deine Arbeitszeit für <br />
              <span class="white--text font-weight-bold" style="text-shadow: 0 0 10px rgba(255, 255, 255, 0.3)">
                {{ $moment(selectedDate).format("MMMM YYYY") }}
              </span>
              einzureichen.
            </div>

            <v-sheet color="rgba(0,0,0,0.3)" class="rounded-lg pa-5 mb-3 border-light backdrop-blur">
              <v-row no-gutters align="center">
                <v-col
                  :cols="employeeStudio.noFixPercentage ? '12' : '6'"
                  :class="{ 'border-right pr-4 text-right': !employeeStudio.noFixPercentage, 'text-center': employeeStudio.noFixPercentage }">
                  <div class="text-caption grey--text text-uppercase font-weight-bold letter-spacing-1 mb-1">Gearbeitet</div>
                  <div class="text-h4 font-weight-bold white--text">
                    {{ dayWorkTime.formattedTime }}<span class="text-h6 grey--text ml-1">h</span>
                  </div>
                </v-col>
                <v-col v-if="!employeeStudio.noFixPercentage" cols="6" class="pl-4 text-left">
                  <div class="text-caption grey--text text-uppercase font-weight-bold letter-spacing-1 mb-1">Differenz</div>
                  <div
                    class="text-h4 font-weight-bold"
                    :class="parseFloat(timeDifference) >= 0 ? 'primary-green--text' : 'error--text'"
                    style="text-shadow: 0 0 15px rgba(0, 0, 0, 0.5)">
                    {{ Math.round(parseFloat(timeDifference) * 100) / 100 }} <span class="text-h6 grey--text ml-1">h</span>
                  </div>
                </v-col>
              </v-row>
            </v-sheet>
            <div class="mb-4" v-if="(isPartialMonthEntry || isPartialMonthExit) && !employeeStudio.noFixPercentage">
              <v-chip v-if="isPartialMonthEntry" color="info" small outlined class="mb-1">
                Eintritt: {{ $moment(employeeStudio.entryDate).format("DD.MM.YYYY") }} (Sollstunden reduziert)
              </v-chip>
              <v-chip v-if="isPartialMonthExit" color="warning" small outlined>
                Austritt: {{ $moment(employeeStudio.exitDate).format("DD.MM.YYYY") }} (Sollstunden reduziert)
              </v-chip>
            </div>

            <v-alert color="rgba(255, 152, 0, 0.1)" class="rounded-lg text-left mb-8" style="border: 1px solid rgba(255, 152, 0, 0.25)" dense>
              <div class="d-flex align-start py-2">
                <v-icon color="#FFB74D" small class="mt-1 mr-3">mdi-alert-outline</v-icon>
                <div class="caption grey--text text--lighten-1" style="line-height: 1.5">
                  Mit der Einreichung der Stunden bestätigt der Mitarbeiter deren Richtigkeit.
                  <strong class="d-block mt-1 orange--text text--lighten-2">Nachträgliche Korrekturen sind nicht möglich.</strong>
                </div>
              </div>
            </v-alert>

            <v-row dense>
              <v-col cols="5">
                <v-btn block x-large text color="grey" class="rounded-lg" @click="showSubmitDialog = false"> Abbrechen </v-btn>
              </v-col>
              <v-col cols="7">
                <v-btn
                  block
                  x-large
                  color="#00963f"
                  class="rounded-lg white--text shadow-glow btn-hover-scale gradient-green"
                  elevation="0"
                  @click="confirmSubmission"
                  :loading="submitLoading">
                  <v-icon left>mdi-check-all</v-icon>
                  Einreichen
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card>
      </div>
    </v-dialog>

    <!-- Dialog für Krankmeldung bestätigen/verneinen -->
    <v-dialog v-model="respondDialog" max-width="500" overlay-opacity="0.9" overlay-color="#050505" persistent>
      <v-card class="rounded-xl glass-card pa-6 text-center" dark v-if="selectedSickEntry">
        <h3 class="text-h5 font-weight-bold white--text mb-4">Abwesenheit bestätigen</h3>
        <p class="grey--text text--lighten-1 mb-6">
          Warst du am <strong>{{ formatSickDaysText(selectedSickEntry.sickDays) }}</strong> abwesend (z.B. krank)?
        </p>

        <v-btn-toggle v-model="employeeConfirmed" mandatory class="mb-6 w-100 d-flex" color="#00963f">
          <v-btn :value="true" class="flex-grow-1 py-4 font-weight-bold text-subtitle-1" height="auto" outlined>
            <v-icon left>mdi-check</v-icon> Ja, ich war abwesend
          </v-btn>
          <v-btn :value="false" class="flex-grow-1 py-4 font-weight-bold text-subtitle-1" height="auto" outlined>
            <v-icon left>mdi-close</v-icon> Nein
          </v-btn>
        </v-btn-toggle>

        <v-slide-y-transition>
          <div v-if="employeeConfirmed === true" class="text-left mb-6">
            <div class="text-caption grey--text text-uppercase font-weight-bold letter-spacing-1 mb-2">Arztzeugnis hochladen (AUB - optional)</div>
            <v-file-input
              v-model="attachmentFile"
              label="Datei auswählen (Bild oder PDF)"
              accept="image/*,application/pdf"
              outlined
              dense
              dark
              prepend-icon="mdi-paperclip"
              color="#00963f"
              hide-details
              class="rounded-lg mb-4"></v-file-input>
          </div>
        </v-slide-y-transition>

        <v-textarea
          v-model="employeeComment"
          label="Kommentar (optional)"
          outlined
          dense
          dark
          rows="3"
          class="rounded-lg mb-6 text-left"
          hide-details></v-textarea>

        <v-row dense>
          <v-col cols="6">
            <v-btn block large text color="grey" class="rounded-lg" @click="closeRespondDialog"> Abbrechen </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn
              block
              large
              color="#00963f"
              class="rounded-lg white--text shadow-glow font-weight-bold"
              @click="submitResponse"
              :loading="respondLoading">
              Senden
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <!-- Absences List Dialog -->
    <v-dialog v-model="showAbsencesDialog" max-width="600px" overlay-opacity="0.9" overlay-color="black">
      <v-card class="rounded-xl" color="#1E1E1E">
        <v-toolbar flat color="transparent" class="pt-2 pb-2">
          <v-toolbar-title class="text-h6 font-weight-bold white--text pl-2">
            <v-icon color="#FFC107" class="mr-2">mdi-calendar-clock</v-icon>
            Meine Abwesenheiten
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="showAbsencesDialog = false" color="grey"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pb-6">
          <div v-if="allAbsences && allAbsences.length > 0">
            <div
              v-for="abs in allAbsences"
              :key="abs.id"
              class="pa-4 rounded-lg mb-3 border-light text-left"
              style="background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.05)">
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="font-weight-bold white--text text-subtitle-1">{{ abs.typeText }}</span>
                <v-chip
                  small
                  :color="abs.status === 'accepted' ? 'success' : abs.status === 'rejected' ? 'error' : 'warning'"
                  class="font-weight-black">
                  {{ abs.status === "accepted" ? "Genehmigt" : abs.status === "rejected" ? "Abgelehnt" : "Ausstehend" }}
                </v-chip>
              </div>
              <div class="grey--text text-body-2 mb-2">
                <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
                {{ abs.dateText }} &nbsp;•&nbsp; {{ abs.details }}
              </div>
              <div
                v-if="abs.note"
                class="grey--text text-body-2 font-italic mb-2"
                style="background: rgba(0, 0, 0, 0.15); padding: 6px 12px; border-left: 3px solid #00963f; border-radius: 4px">
                "{{ abs.note }}"
              </div>

              <!-- Arztzeugnis (AUB) Info and Download -->
              <div v-if="abs.attachment" class="mt-3 pt-3" style="border-top: 1px dashed rgba(255, 255, 255, 0.1)">
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon color="info" class="mr-2">
                      {{ abs.attachment.mimeType && abs.attachment.mimeType.includes("pdf") ? "mdi-file-pdf-box" : "mdi-file-image" }}
                    </v-icon>
                    <div>
                      <div class="white--text text-caption font-weight-bold">{{ abs.attachment.originalName || "Arztzeugnis" }}</div>
                      <div class="grey--text text-caption" v-if="abs.attachment.size">{{ (abs.attachment.size / 1024 / 1024).toFixed(2) }} MB</div>
                    </div>
                  </div>
                  <v-btn
                    small
                    color="#00963f"
                    class="black--text font-weight-bold"
                    @click="downloadAttachment(abs)"
                    :loading="downloadingAbsId === abs.id">
                    <v-icon left small>mdi-download</v-icon> Anzeigen
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-10 grey--text text-body-1">
            <v-icon size="48" color="#444" class="mb-3">mdi-calendar-blank</v-icon>
            <div>Keine Abwesenheiten vorhanden.</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios"
import api from "@/api"
import userService from "@/services/userService"
import BreakRules from "./BreakRules"
import EnterHolidays from "./EnterHolidays"
import EnterFreeWishes from "./EnterFreeWishes"
import EditTimeEntry from "./EditTimeEntry"
import DemandTimeEntryEdit from "./demandTimeEntryEdit"
import TripControlHistory from "./TripControlHistory"
import { mapState, mapActions } from "vuex"
import EnterShiftWishes from "./EnterShiftWishes.vue"

export default {
  name: "EmployeeTimeTracker",
  components: { BreakRules, EnterShiftWishes, EnterHolidays, EnterFreeWishes, EditTimeEntry, TripControlHistory, DemandTimeEntryEdit },
  props: {
    employeeName: String,
    shiftTime: String,
  },
  data() {
    return {
      holidaysData: [],
      showSubmitDialog: false,
      submitLoading: false,
      firmen: ["Firma 1", "Firma 2"],
      selectedFirma: "Firma 1",
      mappedAbsenceTypes: {
        sick: "Krank",
        school: "Schule",
        pregnancy: "Schwangerschaft",
        holidayFix: "Ferien (nachtrag)",
        other: "Anderes",
        military: "Militär",
        holiday: "Ferien",
      },
      studioConfirmed: false,
      userService,
      userStudio: null,
      showDatePicker: false,
      calendarType: "day",
      calendarTypes: [
        { text: "Tag", value: "day" },
        { text: "Woche", value: "week" },
        { text: "Monat", value: "month" },
      ],
      showButtons: false,
      loading: true,
      selectedDate: new Date(),
      checkInTime: null,
      checkOutTime: null,
      dailyNote: "",
      pauseStartTime: null,
      pauseEndTime: null,
      showCorrection: true,
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      events: [],
      recentEntries: [],
      tripControlHistoryEntries: [],
      holidayEntries: [],
      freeWishes: [],
      currentType: null,
      currentIndex: null,
      interval: null,
      timeTrackerMonths: [],
      selfSubmittedTimetrackerMonths: [],
      timeTrackerDailyNotes: [],
      overTime: null,
      officeDuration: { formattedTime: null, totalMinutes: 0 },
      employeeStudio: { percentage: 0, entryDate: null, exitDate: null, pensumHistory: [], noFixPercentage: false },
      pendingSickEntries: [],
      allSickEntries: [],
      respondDialog: false,
      selectedSickEntry: null,
      employeeConfirmed: true,
      respondLoading: false,
      showAbsencesDialog: false,
      downloadingAbsId: null,
    }
  },
  created() {
    this.fetchUserStudio()
    this.fetchEntries()
  },
  beforeDestroy() {
    this.clearTimer()
  },
  watch: {
    calendarType() {
      this.fetchEntries()
    },
  },
  methods: {
    async downloadAttachment(abs) {
      if (!abs.attachment) return
      this.downloadingAbsId = abs.id
      try {
        const response = await axios.get(`/api/timetracker/sick/${abs.id}/attachment`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
          responseType: "blob",
        })
        const blob = new Blob([response.data], { type: abs.attachment.mimeType })
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = downloadUrl
        link.setAttribute("download", abs.attachment.originalName || "arztzeugnis")
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(downloadUrl)
      } catch (err) {
        console.error("Error downloading attachment:", err)
        this.$toast.error("Fehler beim Herunterladen des Arztzeugnisses")
      } finally {
        this.downloadingAbsId = null
      }
    },
    async saveDailyNote() {
      try {
        if (this.timeTrackerDailyNotes.find((noteEntry) => noteEntry.day === this.$moment(this.selectedDate).format("YYYY-MM-DD"))) {
          this.timeTrackerDailyNotes.find((noteEntry) => noteEntry.day === this.$moment(this.selectedDate).format("YYYY-MM-DD")).note = JSON.parse(
            JSON.stringify(this.dailyNote),
          )
        } else {
          this.timeTrackerDailyNotes.push({
            day: this.$moment(this.selectedDate).format("YYYY-MM-DD"),
            note: JSON.parse(JSON.stringify(this.dailyNote)),
          })
        }
        await api.submitTimeTrackerDailyNote(this.timeTrackerDailyNotes)
        this.$toast.success("Notiz wurde gespeichert")
      } catch (e) {
        console.log(e)
      }
    },
    submitForSelectedMonth() {
      this.showSubmitDialog = true
    },

    // Diese Methode führt die eigentliche Aktion aus
    async confirmSubmission() {
      this.submitLoading = true
      try {
        await api.selfSubmitTimetrackerMonth({ month: this.selectedDate })
        this.$toast.success("Monat erfolgreich eingereicht")
        this.showSubmitDialog = false
        await this.fetchEntries()
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Einreichen")
      } finally {
        this.submitLoading = false
      }
    },
    resolveHolidayCode() {
      let studioId = this.employeeStudio ? this.employeeStudio.resolvedStudioId || this.employeeStudio._id || this.employeeStudio.team : null
      if (!studioId) return "TG-FF-FF"

      // 1. Try direct studio lookup (works when _id is a real studio)
      let fullStudio = this.storeStudios.find((std) => std._id && std._id.toString() === studioId.toString())
      if (fullStudio && fullStudio.holidayCode) return fullStudio.holidayCode

      // 2. studioId is likely a team - use _id from employeeStudio if it's a different field
      if (this.employeeStudio._id && this.employeeStudio._id.toString() !== studioId.toString()) {
        let linkedStudio = this.storeStudios.find((std) => std._id && std._id.toString() === this.employeeStudio._id.toString())
        if (linkedStudio && linkedStudio.holidayCode) return linkedStudio.holidayCode
      }

      // 3. Fallback
      return "TG-FF-FF"
    },
    vacationDaysForMonth() {
      if (!this.employeeStudio) return []
      let holidayCode = this.resolveHolidayCode()
      let monthFormat = this.$moment(this.selectedDate).format("YYYY-MM")

      let matchedHolidays = this.holidaysData.filter((holiday) => {
        let start = holiday.startDate && holiday.startDate.$date ? holiday.startDate.$date : holiday.startDate
        if (!start) return false

        if (this.$moment(start).format("YYYY-MM") !== monthFormat) return false

        let holidayDate = this.$moment(start, "YYYY-MM-DD")

        // Skip weekends — already not counted in calcBusinessDays
        const dow = holidayDate.day()
        if (dow === 0 || dow === 6) return false

        let entryDate = this.employeeStudio.entryDate ? this.$moment(this.employeeStudio.entryDate, "YYYY-MM-DD") : null
        let exitDate = this.employeeStudio.exitDate ? this.$moment(this.employeeStudio.exitDate, "YYYY-MM-DD") : null

        if (entryDate && holidayDate.isBefore(entryDate, "day")) return false
        if (exitDate && holidayDate.isAfter(exitDate, "day")) return false

        if (holiday.nationwide) return true
        if (holidayCode && holiday.type === "Public" && holiday.cantons && holiday.cantons.some((c) => holidayCode.startsWith(c))) {
          return true
        }
        return false
      })

      // Add custom studio/team vacation days (feiertage)
      let customVacationDays = []
      let studioId = this.employeeStudio ? this.employeeStudio.resolvedStudioId || this.employeeStudio._id || this.employeeStudio.team : null
      if (studioId && this.storeStudios && this.storeStudios.length > 0) {
        let s = this.storeStudios.find((std) => std._id && std._id.toString() === studioId.toString())
        if ((!s || !s.vacationDays || s.vacationDays.length === 0) && this.employeeStudio && this.employeeStudio._id) {
          s = this.storeStudios.find((std) => std._id && std._id.toString() === this.employeeStudio._id.toString())
        }
        if (s && s.vacationDays) {
          let entryDate = this.employeeStudio.entryDate ? this.$moment(this.employeeStudio.entryDate, "YYYY-MM-DD") : null
          let exitDate = this.employeeStudio.exitDate ? this.$moment(this.employeeStudio.exitDate, "YYYY-MM-DD") : null

          customVacationDays = s.vacationDays
            .filter((d) => {
              let hDate = this.$moment(d.date, "YYYY-MM-DD")
              if (hDate.format("YYYY-MM") !== monthFormat) return false
              if (entryDate && hDate.isBefore(entryDate, "day")) return false
              if (exitDate && hDate.isAfter(exitDate, "day")) return false
              if (hDate.isoWeekday() === 6 || hDate.isoWeekday() === 7) return false
              return true
            })
            .map((d) => ({
              title: d.title,
              date: d.date,
            }))
        }
      }

      const allDays = [
        ...matchedHolidays.map((hol) => ({
          title: hol.name,
          date: hol.startDate && hol.startDate.$date ? hol.startDate.$date : hol.startDate,
        })),
        ...customVacationDays,
      ]

      // Deduplicate by date
      const uniqueDays = []
      const seenDates = new Set()
      for (const day of allDays) {
        const formattedDate = this.$moment(day.date).format("YYYY-MM-DD")
        if (!seenDates.has(formattedDate)) {
          seenDates.add(formattedDate)
          uniqueDays.push(day)
        }
      }
      return uniqueDays
    },
    async fetchUserStudio() {
      try {
        let studio = await api.getUserStudio()
        this.userStudio = studio.studio.name
      } catch (e) {
        this.$toast.error("Es gab ein Fehler beim Studio laden. Bitte versuche es erneut")
      }
    },
    signOut() {
      this.$toast.success("Erfolgreich ausgeloggt")
      localStorage.removeItem("token")
      localStorage.removeItem("user")
      location.reload()
    },
    showStudioSelection() {
      window.app.$root.$children[0].showStudioSelection = true
    },
    downloadTimetrackingExcel() {
      this.$toast.success("Excel wird generiert")
    },
    previousDate() {
      this.$refs.calendar.prev()
      this.fetchEntries()
    },
    nextDate() {
      this.$refs.calendar.next()
      this.fetchEntries()
    },
    clearTimer() {
      this.currentIndex = null
      clearInterval(this.interval)
    },
    startTimer() {
      if (this.currentIndex !== null)
        this.interval = setInterval(() => {
          this.renewCurrent()
        }, 15000)
    },
    renewCurrent() {
      if (this.currentIndex !== null) this.events[this.currentIndex].end = this.$moment().format("YYYY-MM-DD HH:mm")
    },
    getStartEndDate() {
      switch (this.calendarType) {
        case "month":
          return {
            start: this.$moment(this.selectedDate).startOf("month").format("YYYY-MM-DD"),
            end: this.$moment(this.selectedDate).endOf("month").format("YYYY-MM-DD"),
          }
        case "week":
          return {
            start: this.$moment(this.selectedDate).startOf("week").format("YYYY-MM-DD"),
            end: this.$moment(this.selectedDate).endOf("week").format("YYYY-MM-DD"),
          }
        case "day":
          return {
            start: this.$moment(this.selectedDate).format("YYYY-MM-DD"),
            end: this.$moment(this.selectedDate).format("YYYY-MM-DD"),
          }

        default:
          return {
            start: this.$moment().format("YYYY-MM-DD"),
            end: this.$moment().format("YYYY-MM-DD"),
          }
      }
    },
    async fetchEntries(showCorrection = true) {
      this.showCorrection = showCorrection
      let dates = this.getStartEndDate()
      this.loading = true
      this.clearTimer()
      let currentFound = false
      let entries = await api.fetchTimetrackerCalendarEntries(dates, this.showCorrection)
      if (!this.holidaysData.length && api.fetchHolidays) {
        try {
          this.holidaysData = await api.fetchHolidays()
        } catch (e) {
          console.error("Error fetching holidays", e)
        }
      }
      if (
        entries.timeTrackerDailyNotes &&
        entries.timeTrackerDailyNotes.find((noteEntry) => noteEntry.day === this.$moment(this.selectedDate).format("YYYY-MM-DD"))
      ) {
        this.dailyNote = JSON.parse(
          JSON.stringify(
            entries.timeTrackerDailyNotes.find((noteEntry) => noteEntry.day === this.$moment(this.selectedDate).format("YYYY-MM-DD")).note,
          ),
        )
      } else {
        this.dailyNote = ""
      }
      this.timeTrackerDailyNotes = JSON.parse(JSON.stringify(entries.timeTrackerDailyNotes))
      let allTimeEntries = entries.mappedTimeEntries
      this.employeeStudio = entries.studio || { percentage: 0 }
      this.officeDuration = await api.getTimetrackerDurationUser({ selectedDate: [dates.start, dates.end], studio: entries.studio._id })
      this.secondCompany = entries.secondCompany
      this.recentEntries = entries.recentTimeEntries
      this.holidayEntries = entries.holidayEntries
      this.pendingSickEntries = entries.pendingSickEntries || []
      this.allSickEntries = entries.allSickEntries || []
      this.overTime = entries.overTime
      this.tripControlHistoryEntries = entries.tripControlHistoryEntries
      this.showButtons = entries.showButtons
      this.timeTrackerMonths = entries.timeTrackerMonths
      this.selfSubmittedTimetrackerMonths = entries.selfSubmittedTimetrackerMonths
      allTimeEntries = allTimeEntries.map((e, index) => {
        if (e.end === "current") {
          currentFound = true
          this.currentType = e.type
          this.currentIndex = index
          e.end = this.$moment().format("YYYY-MM-DD HH:mm")
        }
        delete e.type
        return e
      })

      this.startTimer()
      let holidayEntriesMapped = []
      let sickEntriesMapped = []
      if (this.holidayEntries[0] !== undefined) {
        holidayEntriesMapped = this.holidayEntries.filter((entry) => entry.status !== "rejected")
        holidayEntriesMapped = holidayEntriesMapped.map((e) => {
          if (e && e.dates && e.dates.length) {
            let isSame = this.$moment(e.dates[0].date).isSame(e.dates[e.dates.length - 1].date, "day")
            return {
              start: this.$moment(e.dates[0].date)
                .set({
                  hours: isSame ? 8 : 17 - e.dates[0].duration,
                  minutes: 0,
                  seconds: 0,
                  milliseconds: 0,
                })
                .format("YYYY-MM-DD HH:mm"),
              end: this.$moment(e.dates[e.dates.length - 1].date)
                .set({
                  hours: 8 + e.dates[e.dates.length - 1].duration,
                  minutes: 0,
                  seconds: 0,
                  milliseconds: 0,
                })
                .format("YYYY-MM-DD HH:mm"),
              color: "#FFC107",
              details: "Ferien",
            }
          }
        })
      }
      if (entries.sickEntries && entries.sickEntries.length) {
        entries.sickEntries.forEach((e) => {
          if (e.status === "rejected") return
          if (e.employeeResponse && e.employeeResponse.confirmed === false && e.status !== "accepted") return
          if (e && e.sickDays && e.sickDays.length) {
            e.sickDays.forEach((day) => {
              if (!this.$moment(day.date).isBetween(this.getStartEndDate().start, this.getStartEndDate().end, null, "[]")) return

              // Wir definieren den Start-Zeitpunkt einmal sauber
              let startMoment = this.$moment(day.date).set({
                hours: 1, // Startzeit wie in deinem Originalcode (01:00 Uhr)
                minutes: 0,
                seconds: 0,
                milliseconds: 0,
              })

              sickEntriesMapped.push({
                // Startzeit formatieren
                start: startMoment.format("YYYY-MM-DD HH:mm"),
                end: startMoment
                  .clone()
                  .add(parseFloat(String(day.hours).replace(",", ".")), "hours")
                  .format("YYYY-MM-DD HH:mm"),

                color: "black",
                details: (this.mappedAbsenceTypes[e.absenceReason] || "") + (e.note ? " - " + e.note : ""),
              })
            })
          }
        })
      }
      allTimeEntries = allTimeEntries.concat(holidayEntriesMapped, sickEntriesMapped)
      this.events = allTimeEntries
      if (!currentFound) {
        this.currentType = null
        this.currentIndex = null
      }
      this.loading = false
    },
    async createTimeEntry(type, reason = null) {
      let smoke = type === "smoke"
      let entry = {
        date: this.$moment().format("YYYY-MM-DD HH:mm"),
        type: smoke ? "pause" : type,
        reason,
        smoke,
      }
      let status = await api.createTimetrackerEntryEmployee(entry)
      if (status) {
        this.fetchEntries()
      }
    },
    // Helper für Kalenderfarben
    getEventColor(event) {
      return event.color || "#00963f"
    },
    handleEventClick({ event }) {},
    handleDateClick({ date }) {
      this.selectedDate = date
    },
    updateSelectedDate(newValue) {
      this.selectedDate = new Date(newValue)
      this.fetchEntries()
    },
    formatDecimalHours(decimalHours) {
      const wholeHours = Math.floor(decimalHours)
      const decimalMinutes = (decimalHours - wholeHours) * 60
      const minutes = Math.round(decimalMinutes)
      const totalMinutes = Math.round(decimalHours * 60)
      const duration = this.$moment.duration(totalMinutes, "minutes")
      const displayHours = Math.floor(totalMinutes / 60)
      const displayMinutes = totalMinutes % 60
      const formattedMinutes = String(displayMinutes).padStart(2, "0")
      return `${displayHours}:${formattedMinutes}`
    },
    formatSickDaysText(sickDays) {
      if (!sickDays || !sickDays.length) return ""
      return sickDays.map((sd) => this.$moment(sd.date).format("DD.MM.YYYY")).join(", ")
    },
    openRespondDialog(entry) {
      this.selectedSickEntry = entry
      this.employeeConfirmed = true
      this.employeeComment = ""
      this.attachmentFile = null
      this.respondDialog = true
    },
    closeRespondDialog() {
      this.respondDialog = false
      this.selectedSickEntry = null
      this.attachmentFile = null
    },
    async submitResponse() {
      if (this.selectedSickEntry === null) return
      this.respondLoading = true
      try {
        const formData = new FormData()
        formData.append("confirmed", this.employeeConfirmed)
        formData.append("comment", this.employeeComment)
        if (this.attachmentFile) {
          formData.append("attachment", this.attachmentFile)
        }
        await api.respondSickEntry(this.selectedSickEntry._id, formData)
        this.$toast.success("Antwort erfolgreich gesendet")
        this.closeRespondDialog()
        await this.fetchEntries()
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Senden der Antwort")
      } finally {
        this.respondLoading = false
      }
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    filteredEvents() {
      if (!this.events || !(this.employeeStudio && this.secondCompany)) {
        return this.events
      }
      return this.events.filter((event) => {
        const eventCompany = event.company || "Firma 1"
        return eventCompany === this.selectedFirma
      })
    },
    submissionState() {
      if (!this.timeTrackerMonths) return null
      if (this.timeTrackerMonths && this.timeTrackerMonths.find((ttm) => ttm.month === this.$moment(this.selectedDate).format("YYYY-MM"))) {
        if (this.timeTrackerMonths.find((ttm) => ttm.month === this.$moment(this.selectedDate).format("YYYY-MM")).state === "onlyNotes") return false
        return this.timeTrackerMonths.find((ttm) => ttm.month === this.$moment(this.selectedDate).format("YYYY-MM"))
      }
    },
    allAbsences() {
      const list = []

      // Map sick days / absences
      if (this.allSickEntries && this.allSickEntries.length) {
        this.allSickEntries.forEach((entry) => {
          if (!entry.sickDays || !entry.sickDays.length) return
          if (
            entry.absenceReason === "holiday" ||
            entry.absenceReason === "holidayFix" ||
            entry.absenceReason === "public_holiday" ||
            entry.absenceReason === "school"
          )
            return
          const sortedDays = [...entry.sickDays].map((d) => d.date).sort()
          const start = sortedDays[0]
          const end = sortedDays[sortedDays.length - 1]
          const totalHours = entry.sickDays.reduce((acc, curr) => acc + parseFloat(String(curr.hours).replace(",", ".")), 0)

          list.push({
            id: entry._id,
            type: entry.absenceReason || "sick",
            typeText: this.mappedAbsenceTypes[entry.absenceReason] || "Krankheit",
            dateText:
              start === end
                ? this.$moment(start).format("DD.MM.YYYY")
                : `${this.$moment(start).format("DD.MM.YYYY")} - ${this.$moment(end).format("DD.MM.YYYY")}`,
            startObj: new Date(start),
            status: entry.status || "accepted", // fallback to accepted for legacy
            details: `${Math.round(totalHours * 100) / 100} Std.`,
            note: entry.note || "",
            attachment: entry.attachment || null,
          })
        })
      }

      // Sort by start date descending (newest first)
      list.sort((a, b) => b.startObj - a.startObj)
      return list
    },
    isMonthLocked() {
      if (!this.submissionState) return false
      return this.submissionState.state === "submitted" || this.submissionState.state === "accepted"
    },
    username() {
      let user = this.userService.getUser()
      if (user && user.vorname && user.nachname) return user.vorname + " " + user.nachname
      else return "Benutzer"
    },
    calcBusinessDays() {
      var day = this.$moment(this.selectedDate).startOf("month")
      var businessDays = 0

      let entryDate = this.employeeStudio.entryDate ? this.$moment(this.employeeStudio.entryDate, "YYYY-MM-DD") : null
      let exitDate = this.employeeStudio.exitDate ? this.$moment(this.employeeStudio.exitDate, "YYYY-MM-DD") : null

      while (day.isSameOrBefore(this.$moment(this.selectedDate).endOf("month"), "day")) {
        let isAfterEntry = !entryDate || day.isSameOrAfter(entryDate, "day")
        let isBeforeExit = !exitDate || day.isSameOrBefore(exitDate, "day")

        if (day.day() != 0 && day.day() != 6 && isAfterEntry && isBeforeExit) {
          businessDays++
        }
        day.add(1, "d")
      }
      return businessDays
    },
    isPartialMonthEntry() {
      if (!this.employeeStudio || !this.employeeStudio.entryDate) return false
      let entry = this.$moment(this.employeeStudio.entryDate)
      let monthStart = this.$moment(this.selectedDate).startOf("month")
      let monthEnd = this.$moment(this.selectedDate).endOf("month")
      return entry.isAfter(monthStart) && entry.isSameOrBefore(monthEnd)
    },
    isPartialMonthExit() {
      if (!this.employeeStudio || !this.employeeStudio.exitDate) return false
      let exit = this.$moment(this.employeeStudio.exitDate)
      let monthStart = this.$moment(this.selectedDate).startOf("month")
      let monthEnd = this.$moment(this.selectedDate).endOf("month")
      return exit.isBefore(monthEnd) && exit.isSameOrAfter(monthStart)
    },
    selectedDateFormatted: {
      get() {
        return this.$moment(this.selectedDate).format("YYYY-MM-DD")
      },
      set(newValue) {
        this.selectedDate = new Date(newValue)
      },
    },
    dayWorkStart() {
      if (!this.filteredEvents || !this.filteredEvents.length || this.filteredEvents[0] === null) return "00:00"
      let firstWorkEvent = this.filteredEvents.find((e) => e.details === "Arbeit")
      if (firstWorkEvent) return this.$moment(firstWorkEvent.start).format("HH:mm")
      else return "00:00"
    },
    dayWorkEnd() {
      if (!this.filteredEvents || !this.filteredEvents.length || this.filteredEvents[0] === null) return "00:00"
      if (this.currentType == "checkin" || this.currentType === "pause") return "00:00"
      let lastWorkEvent = this.filteredEvents
        .slice()
        .reverse()
        .find((e) => e.details === "Arbeit")
      if (lastWorkEvent) return this.$moment(lastWorkEvent.end).format("HH:mm")
      else return "00:00"
    },
    dayPauseTime() {
      if (!this.filteredEvents || !this.filteredEvents.length || this.filteredEvents[0] === null) return "00:00"
      let pauseEvents = this.filteredEvents.filter((e) => e && e.details === "Pause")
      if (!pauseEvents) return "00:00"
      pauseEvents = pauseEvents.reduce((acc, cur) => {
        let duration = this.$moment.duration(this.$moment(cur.end).diff(this.$moment(cur.start)))
        return acc + duration.asMinutes()
      }, 0)
      pauseEvents = Math.round(pauseEvents)
      let hours = Math.floor(pauseEvents / 60)
      let minutes = pauseEvents % 60
      let formattedTime = hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0")
      return formattedTime
    },
    currentMonthPercentage() {
      if (!this.employeeStudio) return 0
      if (this.employeeStudio.noFixPercentage) return 0
      let targetFormat = this.$moment(this.selectedDate).format("YYYY-MM")
      let history = this.employeeStudio.pensumHistory || []
      // Sort history descending by validFrom
      let sortedHistory = history.slice().sort((a, b) => b.validFrom.localeCompare(a.validFrom))
      let applicable = sortedHistory.find((h) => h.validFrom <= targetFormat)
      if (applicable) return applicable.percentage
      return this.employeeStudio.percentage || 0
    },
    isPartialMonthEntry() {
      if (!this.employeeStudio || !this.employeeStudio.entryDate) return false
      const entry = this.$moment(this.employeeStudio.entryDate)
      const monthStart = this.$moment(this.selectedDate).startOf("month")
      const monthEnd = this.$moment(this.selectedDate).endOf("month")
      return entry.isAfter(monthStart) && entry.isSameOrBefore(monthEnd)
    },
    isPartialMonthExit() {
      if (!this.employeeStudio || !this.employeeStudio.exitDate) return false
      const exit = this.$moment(this.employeeStudio.exitDate)
      const monthStart = this.$moment(this.selectedDate).startOf("month")
      const monthEnd = this.$moment(this.selectedDate).endOf("month")
      return exit.isBefore(monthEnd) && exit.isSameOrAfter(monthStart)
    },
    timeDifference() {
      let vacationDaysLength = this.vacationDaysForMonth().length || 0
      return (
        (this.dayWorkTime.minutes - (this.calcBusinessDays - vacationDaysLength) * ((8.4 * 60 * this.currentMonthPercentage) / 100)) / 60 + " Stunden"
      )
    },
    dayWorkTime() {
      if (!this.filteredEvents || !this.filteredEvents.length || this.filteredEvents[0] === null) return { formattedTime: "00:00", minutes: 0 }
      let workEvents = this.filteredEvents.filter(
        (e) =>
          e.details === "Arbeit" ||
          e.details === "Krank" ||
          e.details === "Schule" ||
          e.details === "Schwangerschaft" ||
          e.details === "Ferien (nachtrag)" ||
          e.details === "Anderes" ||
          e.details === "Militär",
      )
      if (!workEvents) return { formattedTime: "00:00", minutes: 0 }

      workEvents = workEvents.reduce((acc, cur) => {
        let duration = this.$moment.duration(this.$moment(cur.end).diff(this.$moment(cur.start)))
        return acc + duration.asMinutes()
      }, 0)

      let start = this.$moment(this.selectedDate).startOf(this.calendarType)
      let end = this.$moment(this.selectedDate).endOf(this.calendarType)
      this.holidayEntries
        .filter(
          (entry) =>
            entry.status !== "rejected" && entry.dates.find((date) => this.$moment(date.date, "YYYY-MM-DD").isBetween(start, end, null, "[]")),
        )
        .forEach((entry) => {
          entry.dates.forEach((date) => {
            if (this.$moment(date.date, "YYYY-MM-DD").isBetween(start, end, null, "[]")) {
              workEvents += date.duration * 60
            }
          })
        })

      workEvents = Math.round(workEvents)
      let hours = Math.floor(workEvents / 60)
      let minutes = workEvents % 60
      let formattedTime = hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0")
      let totalMinutes = hours * 60 + minutes
      if (this.calendarType === "month" && this.officeDuration && this.officeDuration.totalMinutes > 0) {
        totalMinutes = this.officeDuration.totalMinutes
        formattedTime = this.officeDuration.formattedTime
      }
      return { formattedTime: formattedTime, minutes: totalMinutes }
    },
  },
}
</script>

<style scoped lang="scss">
/* --- Variables & Base --- */
.dashboard-wrapper {
  font-family: "Inter", "Roboto", sans-serif;
  color: white;
}

.primary-green--text {
  color: #00963f !important;
}
.glass-card {
  background: rgba(30, 30, 30, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.glass-btn {
  background: rgba(255, 255, 255, 0.1);
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.border-light {
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* --- Gradients & Shadows --- */
.gradient-green {
  background: linear-gradient(145deg, #00963f 0%, #005c27 100%);
  border: none;
}
.gradient-dark {
  background: linear-gradient(145deg, #1a1a1a 0%, #0f0f0f 100%);
}
.shadow-glow {
  box-shadow: 0 0 20px rgba(0, 150, 63, 0.5) !important;
}
.shadow-soft {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5) !important;
}
.border-glow-entry {
  border: 1px solid rgba(0, 150, 63, 0.4);
  box-shadow: 0 0 30px rgba(0, 150, 63, 0.1);
}

/* --- Animations --- */
.btn-hover-scale:hover {
  transform: scale(1.02);
  transition: transform 0.2s ease;
}
.card-hover-lift {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  }
}

/* --- Pulse Animation for Check-in --- */
.pulse-green {
  animation: pulse-green-anim 2s infinite;
}
@keyframes pulse-green-anim {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 25px rgba(0, 150, 63, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0);
  }
}

.pulse-dot {
  width: 12px;
  height: 12px;
  background-color: #00e676;
  border-radius: 50%;
  box-shadow: 0 0 10px #00e676;
  animation: blink 2s infinite;
}
@keyframes blink {
  50% {
    opacity: 0.5;
  }
}

/* --- Quick Actions Styling --- */
.action-btn-wrapper {
  background: #1e1e1e;
  border-radius: 12px;
  border: 1px solid #333;
  transition: 0.2s;
  cursor: pointer;
  &:hover {
    border-color: #00963f;
    background: rgba(30, 30, 30, 0.8);
  }

  /* Override child component button styling deeply */
  ::v-deep .v-btn {
    height: 50px !important;
    width: 50px !important;
    border-radius: 50%;
    background: #2c2c2c !important;
    box-shadow: none !important;
    margin: 0 !important;
    .v-icon {
      color: white !important;
    }
  }
}
.action-label {
  margin-top: 8px;
  font-size: 0.85rem;
  color: #aaa;
  font-weight: 500;
}

/* --- Calendar Overrides --- */
.calendar-wrapper ::v-deep {
  .v-calendar-daily__head {
    background: transparent !important;
    border-bottom: 1px solid #333;
  }
  .v-calendar-daily__day {
    background: transparent !important;
    border-right: 1px solid #333;
    border-bottom: 1px solid #333;
  }
  .v-calendar-weekly__head {
    background: transparent !important;
  }
  .v-calendar-weekly__day {
    background: transparent !important;
    border: 1px solid #222;
  }
  .v-btn--active {
    background-color: #00963f !important;
  }
}
/* Gap helper */
.gap-2 {
  gap: 12px;
}
/* Equal height fix */
.match-height {
  align-items: stretch;
}
/* Erzeugt einen "Halo"-Rand um das Icon, der die Farbe des Dialog-Hintergrunds (Glass) aufnimmt oder simuliert */
.icon-ring {
  border: 6px solid #1e1e1e; /* Farbe deiner Dialog-Hintergrundfarbe oder dunkelgrau */
  /* Optional: Wenn du willst, dass es richtig leuchtet, nimm stattdessen: */
  /* border: 4px solid rgba(255, 255, 255, 0.05); */
}

.text-shadow {
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.backdrop-blur {
  backdrop-filter: blur(5px);
}

.overflow-visible {
  overflow: visible !important;
}

/* Dein existierender Gradient-Green, falls nicht vorhanden */
.gradient-green {
  background: linear-gradient(145deg, #00963f 0%, #005c27 100%);
}
</style>
